import { css } from '@emotion/react'
import {
  ComponentPropsWithoutRef,
  Dispatch,
  SetStateAction,
  useState,
} from 'react'

import { useElementHeight } from '@/hooks/useElementRect'
import { dropdownArrow, mq } from '@/theme/mixins'
import { colors } from '@/theme/variables'

interface Props extends ComponentPropsWithoutRef<'nav'> {
  curriculumYears?:
    | readonly (Queries.CurriculumYearFragment | null)[]
    | null
  activeYear?: Queries.CurriculumYearFragment | null
  setActiveYear: Dispatch<
    SetStateAction<Queries.CurriculumYearFragment | null | undefined>
  >
}

export const CurriculumNav = ({
  curriculumYears,
  activeYear,
  setActiveYear,
  ...props
}: Props): JSX.Element => {
  const [navOpen, setNavOpen] = useState(false)
  const [dropdownRef, setDropdownRef] = useState<HTMLDivElement | null>(
    null
  )
  const dropdownHeight = useElementHeight(dropdownRef)
  const styles = {
    nav: css`
      padding-right: var(--margin);
      border-right: 2px solid ${colors.gray85};
      position: relative;
      ${mq().s} {
        border: none;
        display: grid;
        grid-template-columns: auto 1fr;
        justify-content: flex-start;
        align-items: flex-start;
      }
    `,
    select: css`
      padding: 0.5em 0;
      margin-right: 0.75em;
      line-height: 1;
      font-style: italic;
      color: ${colors.gray45};
      display: none;
      ${mq().s} {
        display: block;
      }
    `,
    selectButton: css`
      color: ${colors.red};
      justify-self: flex-start;
      display: none;
      ${mq().s} {
        display: block;
      }
    `,
    list: css`
      position: sticky;
      top: calc(
        2em + var(--nav-height-flex) + var(--subnav-height, 0px)
      );
      ${mq().s} {
        position: relative;
        top: 0;
        grid-column: 2 / 3;
        overflow: hidden;
        height: ${navOpen ? dropdownHeight : 0}px;
        opacity: ${navOpen ? 1 : 0};
        transition: all 200ms ease-in-out;
        > div {
          padding-bottom: 1em;
        }
      }
    `,
    button: css`
      display: block;
      text-transform: uppercase;
      line-height: 1;
      letter-spacing: 0.1em;
      font-weight: 500;
      padding: 0.5em 0;
      margin-bottom: 0.5em;
      @media (hover: hover) {
        &:hover {
          color: ${colors.red};
        }
      }
    `,
    buttonActive: css`
      color: ${colors.red};
      ${mq().s} {
        display: none;
      }
    `,
  }
  return (
    <nav
      css={styles.nav}
      {...props}
    >
      <span css={styles.select}>Select Class: </span>
      <button
        onClick={() => setNavOpen(prev => !prev)}
        css={[styles.button, styles.selectButton]}
      >
        {activeYear?.year}
        <div css={dropdownArrow(navOpen)} />
      </button>
      <div css={styles.list}>
        <div ref={node => setDropdownRef(node)}>
          {curriculumYears?.map((curriculumYear, i) => (
            <button
              onClick={() => {
                setActiveYear(curriculumYear)
                setNavOpen(false)
              }}
              key={i}
              css={[
                styles.button,
                activeYear === curriculumYear && styles.buttonActive,
              ]}
            >
              {curriculumYear?.year}
            </button>
          ))}
        </div>
      </div>
    </nav>
  )
}
